import { FormEvent } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'baseui/snackbar'

import { italyFiscalCodesManagementActions } from '../../italyFiscalCodesManagementSagas'
import { selectItalyFiscalCodesManagement } from '../../italyFiscalCodesManagementSelectors'
import { closeModal, updateDraft } from '../../italyFiscalCodesManagementSlice'
import { toLocationPayload } from '../../toLocationPayload'

const useLocationForm = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { enqueue } = useSnackbar()
  const { table, draft, fieldErrors, formError, isSaving } = useSelector(selectItalyFiscalCodesManagement)

  const isEdit = draft.id !== null
  const payload = toLocationPayload(draft)
  const codeChanged = isEdit && payload.code !== draft.originalCode
  const isUnchanged = isEdit && !codeChanged && payload.name === draft.originalName
  const canSubmit = payload.code !== '' && payload.name !== '' && !isSaving && !isUnchanged
  const codeError = fieldErrors.code ?? ''
  const nameError = fieldErrors.name ?? ''

  const onCodeChange = (value: string | null) => {
    dispatch(updateDraft({ code: value ?? '' }))
  }

  const onNameChange = (value: string | null) => {
    dispatch(updateDraft({ name: value ?? '' }))
  }

  const close = () => {
    if (isSaving) return
    dispatch(closeModal())
  }

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!canSubmit) return
    dispatch({ type: italyFiscalCodesManagementActions.SAVE, enqueue })
  }

  return {
    codeLabel: t('italyFiscalCodesManagement:modal.code'),
    nameLabel: t('italyFiscalCodesManagement:modal.name'),
    codeHint: t(`italyFiscalCodesManagement:modal.code_hint.${table}`),
    codeChangeWarning: t('italyFiscalCodesManagement:modal.code_change_warning', { code: draft.originalCode }),
    submitLabel: isEdit ? t('common:save') : t('common:create'),
    cancelLabel: t('common:cancel'),
    code: draft.code,
    name: draft.name,
    codeError,
    nameError,
    formError,
    hasCodeError: codeError !== '',
    hasNameError: nameError !== '',
    isFormErrorHidden: formError === '',
    isCodeWarningHidden: !codeChanged,
    isSubmitDisabled: !canSubmit,
    isSaving,
    onCodeChange,
    onNameChange,
    close,
    submit,
  }
}

export default useLocationForm
